import { TruncateChip, TruncateText } from '../../components';
import {
    CreateUserGroupIcon,
    SystemWalletIcon,
    UserGroupIcon,
    UserWalletIcon,
} from '../../assets';
import { alpha } from '@mui/system';
import { themeObject } from 'lib';
import { WALLETS_AND_GROUPS_PATHS } from 'constants/wallets-and-groups-paths';
import { Box } from '@mui/material';
import { TabItemsType } from 'components/landing-page-wrapper/components/TabComponent';

export const tableHeight: number = 260;

export const USER_WALLETS = 'User Wallets';
export const USER_GROUPS = 'User Groups';
export const SYSTEM_WALLETS = 'System Wallets';

export type TabsItemsType =
    | typeof USER_WALLETS
    | typeof USER_GROUPS
    | typeof SYSTEM_WALLETS;

export type ColumnDef = {
    field: string;
    headerName: string;
    width?: number;
    editable?: boolean;
    flex?: number;
    headerClassName?: string;
    resizable?: boolean;
    renderCell?: (params: any) => React.JSX.Element;
};

export const TabItems: TabItemsType[] = [
    { label: USER_WALLETS },
    { label: USER_GROUPS },
    { label: SYSTEM_WALLETS },
];

export const walletsAndGroupsStyles = {
    tabsStyles: {
        minHeight: '32px',
        '& .MuiTabs-scroller': {
            '& .MuiTabs-flexContainer': {
                gap: '12px',
            },
        },
    },
    tabItemStyles: {
        minHeight: '0',
        m: '0px !important',
        p: '2px 20px',
    },
    menuItemStyles: {
        height: '140px',
        '&:hover': {
            backgroundColor: alpha(themeObject.palette.grayScale[50], 1),
        },
    },
    listItemStyles: {
        textWrap: 'wrap',
        marginRight: '12px',
        '& .MuiListItemText-primary': {
            fontWeight: '500 !important',
            fontSize: '24px !important',
            lineHeight: '28.18px !important',
        },
    },
};

export const walletsAndGroupsCommonStyles = {
    containerStyles: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: themeObject.palette.background.default,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        width: '100%',
        borderRadius: '10px',
        mt: '17px',
    },
    textFieldStyles: {
        width: '100%',
        mt: 2,
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    gridStyles: {
        mt: 2,
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    autoCompleteStyles: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    autocompleteOptionsStyles: {
        ...themeObject.typography.Components.menuItem,
        color: themeObject.palette.grayScale[800],
    },
    textFieldContainer: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    createTextFieldStyles: {
        width: '100%',
    },
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        mt: 3,
        mx: '143.5px',
    },
    checkboxHeaderStyles: {
        ...themeObject.typography.Components.menuItem,
        lineHeight: '27px',
        color: themeObject.palette.grayScale[800],
    },
    checkboxSubtitleStyles: {
        ...themeObject.typography.captions.default,
        ...themeObject.typography.captions.regular,
        lineHeight: '15.88px',
        color: themeObject.palette.grayScale[800],
    },
    containerWidth: {
        width: '100%',
        mt: 2,
    },
};

export const getUserGroupIdName = (
    userGroupsData: Array<any>,
    value: any
): string => {
    const filteredGroups = userGroupsData.find(
        (userGroup) => userGroup.id === value
    );
    return filteredGroups ? filteredGroups.name : '';
};

const createWalletColumn = (field: string, headerName: string): ColumnDef => ({
    field,
    headerName,
    editable: false,
    flex: 2,
    width: 150,
    resizable: false,
    renderCell: (params: any): React.JSX.Element => {
        if (!params.value) {
            return <></>;
        }
        return (
            <TruncateChip
                label={params.value}
                variant='outlined'
                color='primary'
                readOnly={true}
                isMultiLineChip={true}
            />
        );
    },
});

export const getUserWalletColumns = (readOnly: boolean): any[] => {
    return [
        {
            field: 'fullName',
            headerName: 'User Name',
            width: 150,
            editable: false,
            flex: 1,
            resizable: !readOnly,
        },
        {
            field: 'email',
            headerName: 'Email Address',
            editable: false,
            flex: 1,
            resizable: !readOnly,
        },
        {
            field: 'id',
            headerName: 'User ID',
            editable: false,
            flex: 1,
            resizable: !readOnly,
        },
    ];
};

export const getUserGroupColumns = (): any[] => {
    return [
        {
            field: 'name',
            headerName: 'Group Name',
            width: 150,
            editable: false,
            flex: 1,
            resizable: false,
        },
        {
            field: 'description',
            headerName: 'Description',
            editable: false,
            flex: 2,
            resizable: false,
            renderCell: (params: any): React.JSX.Element => {
                if (!params.value) {
                    return <></>;
                }
                return (
                    <Box sx={{ wordBreak: 'break-all' }}>
                        <TruncateText value={params.value} numOflines={4} />
                    </Box>
                );
            },
        },
    ];
};

const baseSystemWalletColumns = [
    {
        field: 'type',
        headerName: 'Wallet Type',
        width: 150,
        editable: false,
        flex: 1.5,
        resizable: false,
    },
    {
        field: 'description',
        headerName: 'Description',
        editable: false,
        flex: 1.5,
        width: 150,
        resizable: false,
        renderCell: (params: any): React.JSX.Element => {
            if (!params.value) {
                return <></>;
            }
            return (
                <Box sx={{ wordBreak: 'break-all' }}>
                    <TruncateText value={params.value} numOflines={4} />
                </Box>
            );
        },
    },
];

export const getSystemWalletColumns = (): any[] => {
    return [
        ...baseSystemWalletColumns,
        createWalletColumn(
            'accessControlUserGroupName',
            'Access Control User Group'
        ),
        createWalletColumn('writeUserGroupName', 'Write User Group'),
    ];
};

export const getSystemWalletColumnsForMetadataSearch = (): any[] => {
    return [
        ...baseSystemWalletColumns,
        {
            field: 'accessControlUserGroup',
            headerName: 'Access Control User Group',
            editable: false,
            flex: 1.5,
            width: 150,
            resizable: false,
        },
        {
            field: 'writeUserGroup',
            headerName: 'Write User Group',
            editable: false,
            flex: 1.5,
            width: 150,
            resizable: false,
        },
    ];
};

export const getColumns = (
    selectedTab: TabsItemsType,
    readOnly: boolean
): ColumnDef[] => {
    const columns = {
        [USER_WALLETS]: getUserWalletColumns(readOnly),
        [USER_GROUPS]: getUserGroupColumns(),
        [SYSTEM_WALLETS]: getSystemWalletColumns(),
    };

    return columns[selectedTab];
};

export const walletsAndGroupsConfig: {
    [key: string]: {
        imgSrc: string;
        headingText?: string;
        iconStyles?: React.CSSProperties;
    };
} = {
    'user-wallet': {
        imgSrc: UserWalletIcon,
        headingText: 'Create User Wallet',
        iconStyles: {
            width: '90px',
            height: '98.42px',
        },
    },
    'user-group': {
        imgSrc: CreateUserGroupIcon,
        headingText: 'Create User Group',
        iconStyles: {
            width: '140px',
            height: '98.43px',
        },
    },
    'system-wallet': {
        imgSrc: SystemWalletIcon,
        headingText: 'Create System Wallet',
        iconStyles: {
            width: '90px',
            height: '82.34px',
        },
    },
};

export const menuItems = [
    {
        title: 'User Wallet',
        subtitle:
            'The user wallet is for any “login” that represents an entity (human or machine) that entered the system.',
        icon: UserWalletIcon,
        navigationPath: WALLETS_AND_GROUPS_PATHS.CREATE_NEW_USER_WALLET_PATH,
        styles: {
            width: '90px',
            height: '98.42px',
        },
    },
    {
        title: 'User Group',
        subtitle:
            'A user group is a container for a list (group) of user wallet IDs. ',
        icon: UserGroupIcon,
        navigationPath: WALLETS_AND_GROUPS_PATHS.CREATE_NEW_USER_GROUP_PATH,
        styles: {
            width: '89.24px',
            height: '91.19px',
        },
    },
    {
        title: 'System Wallet',
        subtitle:
            'System wallets are used to enhance the basic ownership models of user ownership or user co-ownership. ',
        icon: SystemWalletIcon,
        navigationPath: WALLETS_AND_GROUPS_PATHS.CREATE_NEW_SYSTEM_WALLET_PATH,
        styles: {
            width: '90px',
            height: '82.34px',
        },
    },
];
